/**
 * FASE 22.7 — NEXT ACTIONS RESOLVER
 *
 * Produces a ranked list of next actions from workflow state + context.
 * Never proposes confirmation — that stays in useKeviaChat.
 */

import { getActiveWorkflows } from "./workflowState";
import { getWorkflowById }    from "./workflowState";
import {
  getWorkflowDefinition,
  WORKFLOW_TYPES,
  STEP_STATUS,
} from "./workflowSchemas";
import { getPriorityWorkflows } from "./workflowPriorities";
import { getNextStepForWorkflow } from "./workflowSelectors";

/**
 * @typedef {Object} NextAction
 * @property {string}      label        — pastoral display text
 * @property {string}      action       — machine key (e.g. "advance_step", "start_workflow")
 * @property {number}      confidence   — 0-1
 * @property {string|null} workflowId
 * @property {string|null} stepId
 * @property {string|null} workflowType — for start_workflow proposals
 */

/**
 * Resolve next actions from current workflow state + optional context.
 * @param {{ workflowId?: string, entityContext?: object, appScope?: object }} opts
 * @returns {NextAction[]}
 */
export function resolveNextActions({ workflowId = null, entityContext = null, appScope = null } = {}) {
  const actions = [];

  // ── Specific workflow actions
  if (workflowId) {
    const wf   = getWorkflowById(workflowId);
    const step = wf ? getNextStepForWorkflow(workflowId) : null;
    if (wf && step) {
      const def = getWorkflowDefinition(wf.type);
      const entityName = wf.related_entities?.[0]?.name;
      actions.push({
        label:       entityName
          ? `Completar: ${step.label} (${entityName})`
          : `Completar: ${step.label}`,
        action:      "advance_step",
        confidence:  0.90,
        workflowId:  wf.id,
        stepId:      step.id,
        workflowType: wf.type,
      });
    }
    if (wf && wf.next_recommended_action) {
      actions.push({
        label:       wf.next_recommended_action,
        action:      "recommended_action",
        confidence:  0.85,
        workflowId:  wf.id,
        stepId:      null,
        workflowType: wf.type,
      });
    }
  }

  // ── Priority workflow next steps (top 3 if no specific workflow)
  if (!workflowId) {
    const topWfs = getPriorityWorkflows(3);
    for (const wf of topWfs) {
      const step = getNextStepForWorkflow(wf.id);
      if (!step) continue;
      const def        = getWorkflowDefinition(wf.type);
      const entityName = wf.related_entities?.[0]?.name;
      actions.push({
        label:       entityName
          ? `${def?.label ?? wf.type} — ${step.label} (${entityName})`
          : `${def?.label ?? wf.type} — ${step.label}`,
        action:      "advance_step",
        confidence:  Math.max(0.60, 0.85 - actions.length * 0.05),
        workflowId:  wf.id,
        stepId:      step.id,
        workflowType: wf.type,
      });
      if (actions.length >= 3) break;
    }
  }

  // ── Contextual: propose starting a workflow if entity context is present
  if (entityContext?.entityType && actions.length < 3) {
    const TYPE_BY_ENTITY = {
      family: WORKFLOW_TYPES.FAMILY_FOLLOWUP,
      person: WORKFLOW_TYPES.INACTIVE_MEMBER_RECOVERY,
      prayer: WORKFLOW_TYPES.PRAYER_FOLLOWUP,
      event:  WORKFLOW_TYPES.EVENT_PREPARATION,
    };
    const suggestedType = TYPE_BY_ENTITY[entityContext.entityType];
    if (suggestedType) {
      const def = getWorkflowDefinition(suggestedType);
      const active = getActiveWorkflows().find(
        (w) => w.type === suggestedType &&
          w.related_entities.some((e) => e.id === entityContext.entityId),
      );
      if (!active && def) {
        actions.push({
          label:        `Iniciar: ${def.label}`,
          action:       "start_workflow",
          confidence:   0.65,
          workflowId:   null,
          stepId:       null,
          workflowType: suggestedType,
        });
      }
    }
  }

  return actions.slice(0, 4);
}
